import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import Button from '../../components/ui/Button';
import { 
  ArrowLeft,
  User,
  Mail,
  Phone,
  MessageSquare,
  Calendar,
  Clock,
  BookOpen,
  Users,
  MapPin,
  DollarSign,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  School,
  Save
} from 'lucide-react';
import { formatTimeSlot } from '../../utils/dateUtils';

const TutorViewPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tutorId } = useParams();
  
  // State
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  // Load tutor data
  useEffect(() => {
    const loadTutorData = async () => {
      if (!user || !tutorId) return;

      try {
        setLoading(true);
        setError(null);

        // Verify admin status
        const adminStatus = await api.admin.verifyAdmin(user.$id);
        if (!adminStatus) {
          navigate('/classes');
          return;
        }
        setIsAdmin(true);

        // Load tutor details
        const tutorData = await api.tutors.getApplication(tutorId);
        if (!tutorData) {
          throw new Error('Tutor application not found');
        }
        setTutor(tutorData);
        setInternalNotes(tutorData.internal_notes || '');

      } catch (err) {
        console.error('Error loading tutor data:', err);
        setError(err.message || 'Failed to load tutor details');
      } finally {
        setLoading(false);
      }
    };

    loadTutorData();
  }, [user, tutorId, navigate]);

  // Get status display
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'pending':
        return {
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-500 bg-opacity-20',
          icon: <Clock size={20} />,
          text: 'Pending Review'
        };
      case 'approved':
        return {
          color: 'text-green-400',
          bgColor: 'bg-green-500 bg-opacity-20',
          icon: <CheckCircle size={20} />,
          text: 'Approved'
        };
      case 'rejected':
        return {
          color: 'text-red-400',
          bgColor: 'bg-red-500 bg-opacity-20',
          icon: <XCircle size={20} />,
          text: 'Rejected'
        };
      default:
        return {
          color: 'text-gray-400',
          bgColor: 'bg-gray-500 bg-opacity-20',
          icon: <AlertCircle size={20} />,
          text: 'Unknown'
        };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Get WhatsApp link
  const getWhatsAppLink = (phone) => {
    if (!phone) return '';
    const digitsOnly = phone.replace(/\D/g, '');
    return `https://wa.me/${digitsOnly}`;
  };

  // Handle save internal notes
  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      setError(null);
      
      const response = await api.tutors.updateInternalNotes(tutorId, internalNotes);
      
      if (response.success) {
        // Update local state
        setTutor(prev => ({
          ...prev,
          internal_notes: internalNotes
        }));
      } else {
        throw new Error(response.message || 'Failed to save notes');
      }
    } catch (err) {
      console.error('Error saving notes:', err);
      setError(err.message || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    if (newStatus === tutor.status) {
      return; // Already this status
    }

    const confirmMessage = `Are you sure you want to change the status to "${newStatus}"?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setChangingStatus(true);
      setError(null);
      
      const response = await api.tutors.updateStatus(tutorId, newStatus);
      
      if (response.success) {
        // Reload tutor data
        const updatedTutor = await api.tutors.getApplication(tutorId);
        setTutor(updatedTutor);
      } else {
        throw new Error(response.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.message || 'Failed to update status');
    } finally {
      setChangingStatus(false);
    }
  };

  // Handle back
  const handleBack = () => {
    navigate('/admin/tutors');
  };

  // Parse availability
  const getAvailabilityDisplay = () => {
    if (!tutor?.availability) return null;

    try {
      const availability = typeof tutor.availability === 'string' 
        ? JSON.parse(tutor.availability) 
        : tutor.availability;

      // Group by day
      const grouped = {};
      Object.keys(availability).forEach(slot => {
        if (availability[slot]) {
          const [day, time] = slot.split('-');
          if (!grouped[day]) {
            grouped[day] = [];
          }
          grouped[day].push(formatTimeSlot(slot).split(' at ')[1]);
        }
      });

      return grouped;
    } catch (e) {
      console.error('Error parsing availability:', e);
      return null;
    }
  };

  const availabilityDisplay = getAvailabilityDisplay();

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-purple-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-400">Loading tutor details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !tutor) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
        <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
          <div className="container mx-auto p-4 flex justify-between items-center">
            <h1 className="text-xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
                Admin Dashboard
              </span>
            </h1>
          </div>
          <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
        </header>

        <main className="container mx-auto py-8 px-4">
          <div className="max-w-md mx-auto text-center">
            <XCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Error Loading Application</h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <Button variant="primary" onClick={handleBack}>
              Back to Tutors
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (!tutor) {
    return null;
  }

  const statusDisplay = getStatusDisplay(tutor.status);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold">
            <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
              Admin Dashboard
            </span>
          </h1>
          
          <div className="flex items-center space-x-4">
            <span className="text-gray-300 text-sm hidden sm:inline">
              Admin: {user?.name}
            </span>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Back button and status buttons */}
          <div className="flex justify-between items-center mb-6">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-gray-400 hover:text-white flex items-center"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Tutors
            </Button>

            {/* Status Buttons */}
            <div className="flex items-center space-x-3">
              <Button
                variant={tutor.status === 'pending' ? 'primary' : 'outline'}
                size="small"
                onClick={() => handleStatusChange('pending')}
                disabled={changingStatus}
                className={
                  tutor.status === 'pending'
                    ? 'bg-yellow-600 hover:bg-yellow-700 border-yellow-600 flex items-center'
                    : 'border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:bg-opacity-10 flex items-center'
                }
              >
                <Clock size={14} className="mr-1" />
                {tutor.status === 'pending' ? 'Pending' : 'Mark Pending'}
              </Button>

              <Button
                variant={tutor.status === 'approved' ? 'primary' : 'outline'}
                size="small"
                onClick={() => handleStatusChange('approved')}
                disabled={changingStatus}
                className={
                  tutor.status === 'approved'
                    ? 'bg-green-600 hover:bg-green-700 border-green-600 flex items-center'
                    : 'border-green-500 text-green-400 hover:bg-green-500 hover:bg-opacity-10 flex items-center'
                }
              >
                <CheckCircle size={14} className="mr-1" />
                {tutor.status === 'approved' ? 'Approved' : 'Approve'}
              </Button>

              <Button
                variant={tutor.status === 'rejected' ? 'primary' : 'outline'}
                size="small"
                onClick={() => handleStatusChange('rejected')}
                disabled={changingStatus}
                className={
                  tutor.status === 'rejected'
                    ? 'bg-red-600 hover:bg-red-700 border-red-600 flex items-center'
                    : 'border-red-500 text-red-400 hover:bg-red-500 hover:bg-opacity-10 flex items-center'
                }
              >
                <XCircle size={14} className="mr-1" />
                {tutor.status === 'rejected' ? 'Rejected' : 'Reject'}
              </Button>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {/* Page title */}
          <div className="text-center mb-8">
            <div className="inline-block text-6xl font-light text-purple-400 opacity-20 mb-2">
              导师详情
            </div>
            <h2 className="text-2xl font-bold">Tutor Application Details</h2>
            <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
          </div>

          {/* Profile Overview Card */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-purple-800 to-purple-700 px-6 py-4 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg flex items-center">
                  <User size={20} className="mr-2" />
                  Profile Overview
                </h3>
                <span className={`flex items-center px-4 py-2 rounded-full text-sm font-medium ${statusDisplay.bgColor} ${statusDisplay.color}`}>
                  {statusDisplay.icon}
                  <span className="ml-2">{statusDisplay.text}</span>
                </span>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-start space-x-6 mb-6">
                {/* Profile Photo */}
                <div className="flex-shrink-0">
                  {tutor.profilePhotoId ? (
                    <img 
                      src={`${process.env.REACT_APP_APPWRITE_ENDPOINT}/storage/buckets/tutor-files/files/${tutor.profilePhotoId}/view?project=${process.env.REACT_APP_APPWRITE_PROJECT_ID}`}
                      alt={`${tutor.firstName} ${tutor.lastName}`}
                      className="w-32 h-32 rounded-lg object-cover border-2 border-purple-500"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-purple-600 rounded-lg flex items-center justify-center">
                      <User size={48} className="text-white" />
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="flex-1">
                  <h2 className="text-3xl font-bold mb-2">
                    {tutor.firstName} {tutor.lastName}
                  </h2>
                  <p className="text-purple-300 text-lg mb-4">
                    {tutor.yearsExperience} Years of Teaching Experience
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center text-gray-300">
                      <Mail size={16} className="mr-2 text-purple-400" />
                      <a href={`mailto:${tutor.email}`} className="hover:text-white">
                        {tutor.email}
                      </a>
                    </div>

                    <div className="flex items-center text-gray-300">
                      <Phone size={16} className="mr-2 text-purple-400" />
                      <a href={`tel:${tutor.phoneNumber}`} className="hover:text-white">
                        {tutor.phoneNumber}
                      </a>
                    </div>

                    <div className="flex items-center text-gray-300">
                      <Calendar size={16} className="mr-2 text-purple-400" />
                      Applied: {formatDate(tutor.$createdAt)}
                    </div>

                    {tutor.websiteConsent && (
                      <div className="flex items-center text-green-400">
                        <CheckCircle size={16} className="mr-2" />
                        Website consent given
                      </div>
                    )}
                  </div>

                  {/* Contact Actions */}
                  <div className="mt-4 flex space-x-3">
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => window.open(`mailto:${tutor.email}`, '_blank')}
                      className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:bg-opacity-10 flex items-center"
                    >
                      <Mail size={14} className="mr-2" />
                      Email
                    </Button>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => window.open(`tel:${tutor.phoneNumber}`, '_self')}
                      className="border-green-500 text-green-400 hover:bg-green-500 hover:bg-opacity-10 flex items-center"
                    >
                      <Phone size={14} className="mr-2" />
                      Call
                    </Button>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => window.open(getWhatsAppLink(tutor.phoneNumber), '_blank')}
                      className="border-teal-500 text-teal-400 hover:bg-teal-500 hover:bg-opacity-10 flex items-center"
                    >
                      <MessageSquare size={14} className="mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                </div>
              </div>

              {/* Bio */}
              {tutor.tutorBio && (
                <div className="border-t border-gray-700 pt-6">
                  <h4 className="text-purple-300 font-medium mb-2">Bio</h4>
                  <p className="text-gray-300">{tutor.tutorBio}</p>
                </div>
              )}
            </div>
          </div>

          {/* Internal Notes */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-orange-800 to-orange-700 px-6 py-4 border-b border-gray-700">
              <h3 className="font-semibold text-lg flex items-center">
                <FileText size={20} className="mr-2" />
                Internal Notes (Admin Only)
              </h3>
            </div>
            
            <div className="p-6">
              <textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Add internal notes about this tutor application (visible to admins only)..."
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[120px] resize-y"
              />
              <div className="mt-4 flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleSaveNotes}
                  isLoading={savingNotes}
                  className="bg-orange-600 hover:bg-orange-700 flex items-center"
                >
                  <Save size={16} className="mr-2" />
                  Save Notes
                </Button>
              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Teaching Profile */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-800 to-blue-700 px-6 py-4 border-b border-gray-700">
                <h3 className="font-semibold text-lg flex items-center">
                  <School size={20} className="mr-2" />
                  Teaching Profile
                </h3>
              </div>
              
              <div className="p-6 space-y-4">
                {tutor.teachingStyles && tutor.teachingStyles.length > 0 && (
                  <div>
                    <p className="text-purple-300 text-sm font-medium mb-2">Teaching Styles</p>
                    <div className="flex flex-wrap gap-2">
                      {tutor.teachingStyles.map((style, idx) => (
                        <span key={idx} className="bg-purple-900 bg-opacity-30 text-purple-300 px-3 py-1 rounded-full text-sm">
                          {style}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {tutor.otherTeachingStyle && (
                  <div>
                    <p className="text-purple-300 text-sm font-medium mb-1">Other Teaching Style</p>
                    <p className="text-gray-300 text-sm">{tutor.otherTeachingStyle}</p>
                  </div>
                )}

                {tutor.ageGroups && tutor.ageGroups.length > 0 && (
                  <div>
                    <p className="text-purple-300 text-sm font-medium mb-2">Age Groups</p>
                    <div className="flex flex-wrap gap-2">
                      {tutor.ageGroups.map((age, idx) => (
                        <span key={idx} className="bg-blue-900 bg-opacity-30 text-blue-300 px-3 py-1 rounded-full text-sm">
                          {age.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {tutor.studentLanguages && tutor.studentLanguages.length > 0 && (
                  <div>
                    <p className="text-purple-300 text-sm font-medium mb-2">Student Languages</p>
                    <div className="space-y-1">
                      {tutor.studentLanguages.map((lang, idx) => (
                        <p key={idx} className="text-gray-300 text-sm">• {lang}</p>
                      ))}
                    </div>
                  </div>
                )}

                {tutor.classFormats && tutor.classFormats.length > 0 && (
                  <div>
                    <p className="text-purple-300 text-sm font-medium mb-2">Class Formats</p>
                    <div className="space-y-1">
                      {tutor.classFormats.map((format, idx) => (
                        <p key={idx} className="text-gray-300 text-sm">• {format}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Compensation & Logistics */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-green-800 to-green-700 px-6 py-4 border-b border-gray-700">
                <h3 className="font-semibold text-lg flex items-center">
                  <DollarSign size={20} className="mr-2" />
                  Compensation & Logistics
                </h3>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <p className="text-purple-300 text-sm font-medium mb-2">Proposed Hourly Rates</p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center bg-gray-700 bg-opacity-50 p-3 rounded">
                      <span className="text-gray-300">Online</span>
                      <span className="text-green-400 font-bold">HKD {tutor.hourlyRateOnline}/hr</span>
                    </div>
                    {tutor.hourlyRateInPerson && (
                      <div className="flex justify-between items-center bg-gray-700 bg-opacity-50 p-3 rounded">
                        <span className="text-gray-300">In-Person</span>
                        <span className="text-green-400 font-bold">HKD {tutor.hourlyRateInPerson}/hr</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-purple-300 text-sm font-medium mb-2">Location Preference</p>
                  <div className="flex items-center text-gray-300">
                    <MapPin size={16} className="mr-2 text-purple-400" />
                    {tutor.locationPreference === 'online_only' && 'Online Only'}
                    {tutor.locationPreference === 'in_person_only' && 'In-Person Only'}
                    {tutor.locationPreference === 'both' && 'Online & In-Person'}
                  </div>
                </div>

                {tutor.baseDistricts && tutor.baseDistricts.length > 0 && (
                  <div>
                    <p className="text-purple-300 text-sm font-medium mb-2">Base Districts</p>
                    <div className="flex flex-wrap gap-2">
                      {tutor.baseDistricts.map((district, idx) => (
                        <span key={idx} className="bg-green-900 bg-opacity-30 text-green-300 px-3 py-1 rounded-full text-sm">
                          {district}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Availability */}
          {availabilityDisplay && Object.keys(availabilityDisplay).length > 0 && (
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-6">
              <div className="bg-gradient-to-r from-yellow-800 to-yellow-700 px-6 py-4 border-b border-gray-700">
                <h3 className="font-semibold text-lg flex items-center">
                  <Clock size={20} className="mr-2" />
                  Availability
                </h3>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(availabilityDisplay).map(([day, times]) => (
                    <div key={day} className="bg-gray-700 bg-opacity-50 p-4 rounded-lg">
                      <h4 className="text-purple-300 font-medium mb-2">{day}</h4>
                      <div className="space-y-1">
                        {times.map((time, idx) => (
                          <p key={idx} className="text-gray-300 text-sm">
                            <span className="font-mono">└─</span> {time}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {tutor.availabilityNotes && (
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <p className="text-purple-300 text-sm font-medium mb-1">Additional Notes</p>
                    <p className="text-gray-300 text-sm">{tutor.availabilityNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Curriculum & Materials */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-indigo-800 to-indigo-700 px-6 py-4 border-b border-gray-700">
              <h3 className="font-semibold text-lg flex items-center">
                <BookOpen size={20} className="mr-2" />
                Curriculum & Materials
              </h3>
            </div>
            
            <div className="p-6 space-y-6">
              {tutor.curriculums && tutor.curriculums.length > 0 && (
                <div>
                  <p className="text-purple-300 text-sm font-medium mb-2">Curriculums & Exams</p>
                  <div className="flex flex-wrap gap-2">
                    {tutor.curriculums.map((curr, idx) => (
                      <span key={idx} className="bg-indigo-900 bg-opacity-30 text-indigo-300 px-3 py-1 rounded-full text-sm">
                        {curr.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {tutor.otherCurriculum && (
                <div>
                  <p className="text-purple-300 text-sm font-medium mb-1">Other Curriculum</p>
                  <p className="text-gray-300 text-sm">{tutor.otherCurriculum}</p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {tutor.textbooks && (
                  <div>
                    <p className="text-purple-300 text-sm font-medium mb-1">Textbooks</p>
                    <p className="text-gray-300 text-sm">{tutor.textbooks}</p>
                  </div>
                )}

                {tutor.digitalTools && (
                  <div>
                    <p className="text-purple-300 text-sm font-medium mb-1">Digital Tools</p>
                    <p className="text-gray-300 text-sm">{tutor.digitalTools}</p>
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <p className="text-purple-300 text-sm font-medium mb-1">Material Support</p>
                  <p className="text-gray-300 text-sm capitalize">{tutor.materialSupport?.replace(/_/g, ' ')}</p>
                </div>

                <div>
                  <p className="text-purple-300 text-sm font-medium mb-1">Can Teach Without Textbook</p>
                  <p className={`text-sm ${tutor.noTextbook ? 'text-green-400' : 'text-red-400'}`}>
                    {tutor.noTextbook ? 'Yes' : 'No'}
                  </p>
                </div>

                <div>
                  <p className="text-purple-300 text-sm font-medium mb-1">Can Create Custom Materials</p>
                  <p className={`text-sm ${tutor.customMaterials ? 'text-green-400' : 'text-red-400'}`}>
                    {tutor.customMaterials ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700">
              <h3 className="font-semibold text-lg flex items-center">
                <FileText size={20} className="mr-2" />
                Documents
              </h3>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {tutor.profilePhotoId && (
                  <a
                    href={`${process.env.REACT_APP_APPWRITE_ENDPOINT}/storage/buckets/tutor-files/files/${tutor.profilePhotoId}/view?project=${process.env.REACT_APP_APPWRITE_PROJECT_ID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center p-4 bg-gray-700 bg-opacity-50 rounded-lg hover:bg-opacity-70 transition-colors"
                  >
                    <User size={20} className="mr-2 text-purple-400" />
                    <span className="text-white">View Profile Photo</span>
                    <Download size={16} className="ml-2 text-gray-400" />
                  </a>
                )}

                {tutor.resumeId && (
                  <a
                    href={`${process.env.REACT_APP_APPWRITE_ENDPOINT}/storage/buckets/tutor-files/files/${tutor.resumeId}/view?project=${process.env.REACT_APP_APPWRITE_PROJECT_ID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center p-4 bg-gray-700 bg-opacity-50 rounded-lg hover:bg-opacity-70 transition-colors"
                  >
                    <FileText size={20} className="mr-2 text-blue-400" />
                    <span className="text-white">View Resume</span>
                    <Download size={16} className="ml-2 text-gray-400" />
                  </a>
                )}

                {tutor.sampleLessonId && (
                  <a
                    href={`${process.env.REACT_APP_APPWRITE_ENDPOINT}/storage/buckets/tutor-files/files/${tutor.sampleLessonId}/view?project=${process.env.REACT_APP_APPWRITE_PROJECT_ID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center p-4 bg-gray-700 bg-opacity-50 rounded-lg hover:bg-opacity-70 transition-colors"
                  >
                    <BookOpen size={20} className="mr-2 text-green-400" />
                    <span className="text-white">View Sample Lesson</span>
                    <Download size={16} className="ml-2 text-gray-400" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          {tutor.notes && (
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-6">
              <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700">
                <h3 className="font-semibold text-lg flex items-center">
                  <AlertCircle size={20} className="mr-2" />
                  Additional Notes (From Applicant)
                </h3>
              </div>
              
              <div className="p-6">
                <p className="text-gray-300">{tutor.notes}</p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TutorViewPage;